import { useTranslation } from "@redotech/redo-i18n";
import { RETURNS_ROUTING_DECISION_STEP_ID } from "@redotech/redo-onboarding-api/returns-routing";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import { AiSlidePanelProvider } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { toast } from "@redotech/redo-web/alert";
import { Flex } from "@redotech/redo-web/flex";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { PortalPreviewStateProvider } from "../../contexts/portal-preview-state-context";
import type { ProductConfig } from "../../contexts/product-config-context";
import { ProductConfigContext } from "../../contexts/product-config-context";
import {
  setFormData as setFormDataStore,
  setProductConfig as setProductConfigStore,
} from "../../contexts/product-config-store";
import type { ProgressData } from "../../contexts/progress-context";
import {
  ProgressContext,
  useProgressUpdater,
} from "../../contexts/progress-context";
import { useAuth } from "../../core/auth-provider";
import { exitToLanding } from "../../core/exit-to-landing";
import { persistCurrentStepBeforeExit } from "../../core/persist-current-step-before-exit";
import { useMerchantInfo } from "../../hooks/use-merchant-info";
import { useOnboardingTracking } from "../../hooks/use-onboarding-tracking";
import { useProduct } from "../../hooks/use-product";
import { persistReturnsResumeSource } from "../../returns-resume-source";
import {
  buildReturnsRoutingDecision,
  persistReturnsRoutingDecision,
} from "../../returns-routing-decision";
import { OnboardingV2RpcClientContext } from "../../rpc-client";
import { STEP_SKELETONS } from "../../step-skeletons";
import { GlobalAskRedo } from "../ask-redo/global-ask-redo";
import { StepSkeleton } from "../ui/step-skeleton";
import { LandingHeader } from "./landing-header";
import * as shellStyles from "./onboarding-shell.module.css";
import { ProductHeader } from "./product-header";

const MarketingRpcProvider = lazy(() =>
  import("../../steps/contextual/contexts/marketing-rpc-provider").then(
    (m) => ({ default: m.MarketingRpcProvider }),
  ),
);

export const ProductLayout = memo(function ProductLayout() {
  const { t } = useTranslation();
  const product = useProduct();
  const { stepPath } = useParams<{ stepPath: string }>();
  const navigate = useNavigate();
  const { merchantInfo } = useMerchantInfo();
  const auth = useAuth();
  const rpcClient = useContext(OnboardingV2RpcClientContext);

  const [productConfig, setProductConfig] = useState<ProductConfig>({});
  const [progressData, setProgressData] = useState<ProgressData>({
    formData: {},
    currentStepId: null,
  });
  const [ready, setReady] = useState(false);
  const [routingPollingFinished, setRoutingPollingFinished] = useState(false);
  const fetchedRef = useRef<string | null>(null);
  const initialAskRedoThreadIdRef = useRef<string | undefined>(undefined);
  const routingDecisionSavingRef = useRef(false);
  const updateFormData = useProgressUpdater(setProgressData);
  const { trackRoutingDecision } = useOnboardingTracking();
  const currentStep = product?.steps.find((s) => s.path === stepPath);

  useEffect(() => {
    if (!product || !rpcClient) {
      return;
    }
    if (fetchedRef.current === product.id) {
      return;
    }
    fetchedRef.current = product.id;

    let cancelled = false;

    rpcClient
      .getProgress({ productId: product.id })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const config: ProductConfig = result.productConfig
          ? {
              bookingUrl: result.bookingUrl,
              helpBookingUrl: result.helpBookingUrl,
              chargebacks: result.productConfig.chargebacks,
              orderEditing: result.productConfig.orderEditing,
              returns: result.productConfig.returns,
              adminConfig: result.adminConfig,
              dealSizing: result.dealSizing,
            }
          : {
              bookingUrl: result.bookingUrl,
              helpBookingUrl: result.helpBookingUrl,
              adminConfig: result.adminConfig,
              dealSizing: result.dealSizing,
            };
        setProductConfig(config);
        setProductConfigStore(config);
        setProgressData({
          formData: result.progress?.formData ?? {},
          currentStepId: result.progress?.currentStepId ?? null,
        });
        initialAskRedoThreadIdRef.current =
          result.progress?.activeAskRedoThreadId ?? undefined;
        if (product.id === "returns" && auth?.resumeSource && result.progress) {
          void persistReturnsResumeSource({
            currentStepId: result.progress.currentStepId,
            resumeSource: auth.resumeSource,
            rpcClient,
          }).catch((error) => {
            console.error("Failed to persist Returns resume source", error);
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load onboarding progress", err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.resumeSource, product, rpcClient]);

  // Prefer Temporal's authoritative volume before falling back. The pull
  // typically writes in <90s, so 15s × 12 leaves sufficient headroom.
  useEffect(() => {
    if (!product || !rpcClient || product.id !== "returns") {
      return;
    }
    if (productConfig.dealSizing?.totalAnnualOrderVolume != null) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 12;
    const INTERVAL_MS = 15_000;

    const intervalId = window.setInterval(async () => {
      if (cancelled) {
        return;
      }
      attempts += 1;
      try {
        const result = await rpcClient.getProgress({ productId: product.id });
        if (cancelled) {
          return;
        }
        const updatedDealSizing = result.dealSizing;
        if (updatedDealSizing != null) {
          setProductConfig((prev) => {
            const next = { ...prev, dealSizing: updatedDealSizing };
            setProductConfigStore(next);
            return next;
          });
        }
        if (updatedDealSizing?.totalAnnualOrderVolume != null) {
          window.clearInterval(intervalId);
        } else if (attempts >= MAX_ATTEMPTS) {
          setRoutingPollingFinished(true);
          window.clearInterval(intervalId);
        }
      } catch {
        if (attempts >= MAX_ATTEMPTS) {
          setRoutingPollingFinished(true);
          window.clearInterval(intervalId);
        }
      }
    }, INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [product, rpcClient, productConfig.dealSizing?.totalAnnualOrderVolume]);

  useEffect(() => {
    const totalAnnualOrderVolume =
      productConfig.dealSizing?.totalAnnualOrderVolume ?? null;
    const provisionalOrderCount30d =
      productConfig.dealSizing?.provisionalOrderCount30d ?? null;
    const hasStoredDecision = Object.hasOwn(
      progressData.formData,
      RETURNS_ROUTING_DECISION_STEP_ID,
    );
    if (
      !ready ||
      product?.id !== "returns" ||
      !rpcClient ||
      !currentStep ||
      hasStoredDecision ||
      routingDecisionSavingRef.current ||
      (totalAnnualOrderVolume == null && !routingPollingFinished)
    ) {
      return;
    }

    routingDecisionSavingRef.current = true;
    const decision = buildReturnsRoutingDecision(
      totalAnnualOrderVolume,
      provisionalOrderCount30d,
    );
    void persistReturnsRoutingDecision({
      currentStepId: currentStep.id,
      decision,
      rpcClient,
      track: trackRoutingDecision,
    })
      .then((written) => {
        if (written) {
          updateFormData(RETURNS_ROUTING_DECISION_STEP_ID, decision);
        }
      })
      .catch(() => {
        routingDecisionSavingRef.current = false;
      });
  }, [
    currentStep,
    product,
    productConfig.dealSizing?.provisionalOrderCount30d,
    productConfig.dealSizing?.totalAnnualOrderVolume,
    progressData.formData,
    ready,
    routingPollingFinished,
    rpcClient,
    trackRoutingDecision,
    updateFormData,
  ]);

  // Keep the formData store in sync so shouldSkip callbacks can read it
  useEffect(() => {
    setFormDataStore(progressData.formData);
  }, [progressData.formData]);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const progressValue = useMemo(
    () => ({ ...progressData, updateFormData, isAnalyzing, setIsAnalyzing }),
    [progressData, updateFormData, isAnalyzing],
  );
  const handleExitToLanding = useCallback(async () => {
    await exitToLanding({
      persist:
        rpcClient && product && currentStep
          ? () =>
              persistCurrentStepBeforeExit({
                currentStepId: currentStep.id,
                formData: progressData.formData,
                productId: product.id,
                rpcClient,
              })
          : undefined,
      navigate: () => navigate("/", { replace: true }),
      onPersistFailure: () =>
        toast(t("Failed to save progress. Please try again."), {
          variant: "error",
        }),
    });
  }, [currentStep, navigate, product, progressData.formData, rpcClient, t]);

  if (!product) {
    return <Navigate replace to="/" />;
  }

  const stepId = currentStep?.id ?? stepPath ?? "";
  const stepLabel = currentStep?.label ?? stepPath ?? "";
  const skeletonConfig = stepId ? STEP_SKELETONS[stepId] : undefined;
  const returnsPortalLogo =
    progressData.formData["ret-portal-preview"]?.portalLogo;
  const headerLogoUrl =
    product.id === "returns" && typeof returnsPortalLogo === "string"
      ? returnsPortalLogo
      : merchantInfo?.logoUrl;
  const askRedoPanel = (
    <GlobalAskRedo
      initialThreadIdRef={initialAskRedoThreadIdRef}
      product={product.id}
      stepId={stepId}
      stepLabel={stepLabel}
    />
  );
  const stepContent = !ready ? (
    <StepSkeleton config={skeletonConfig} />
  ) : (
    <ProductConfigContext.Provider value={productConfig}>
      <PortalPreviewStateProvider>
        {product.id === "contextual" ? (
          <Suspense fallback={<StepSkeleton config={skeletonConfig} />}>
            <MarketingRpcProvider>
              <Outlet context={{ product }} />
            </MarketingRpcProvider>
          </Suspense>
        ) : (
          <Outlet context={{ product }} />
        )}
      </PortalPreviewStateProvider>
    </ProductConfigContext.Provider>
  );
  const productShell =
    product.id === "returns" || product.id === "shipping-fulfillment" ? (
      <AiSlidePanelLayout noRounding panel={askRedoPanel}>
        <Flex
          bgColor="secondary"
          className={shellStyles.elevatedShell}
          dir="column"
        >
          <Flex
            bgColor="primary"
            borderColor="primary"
            borderRadius="2xl"
            borderStyle="solid"
            borderWidth="1px"
            className={shellStyles.elevatedFrame}
            dir="column"
            flex={1}
            minH="none"
            overflow="hidden"
            shadow="lg"
          >
            <LandingHeader
              email={merchantInfo?.email}
              exitText={t("Save and exit")}
              logoUrl={headerLogoUrl}
              onExit={handleExitToLanding}
              showAskRedo
              storeName={merchantInfo?.storeName}
            />
            {stepContent}
          </Flex>
        </Flex>
      </AiSlidePanelLayout>
    ) : (
      <div className={shellStyles.shell}>
        <ProductHeader
          logoUrl={headerLogoUrl}
          product={product}
          showProgress
          storeName={merchantInfo?.storeName}
        />
        <AiSlidePanelLayout noRounding panel={askRedoPanel}>
          {stepContent}
        </AiSlidePanelLayout>
      </div>
    );

  return (
    <ProgressContext.Provider value={progressValue}>
      <AiSlidePanelProvider>{productShell}</AiSlidePanelProvider>
    </ProgressContext.Provider>
  );
});
